export interface NormalisedPhoneNumber {
  value: string;
  isValid: boolean;
  error?: string;
}

export const isValidEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const isValidMultiEmail = (emails: string): boolean => {
  if (!emails) return true;
  const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
  return emailList.every(email => isValidEmail(email));
};

export const isValidABN = (abn: string): boolean => {
  if (!abn) return true;
  const cleanABN = abn.replace(/\s/g, '');
  return cleanABN.length === 11 && /^\d+$/.test(cleanABN);
};

export const validateRequired = (value: any): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

export const validateAndDedupeRecipients = (
  toRecipients: string[],
  ccRecipients: string[],
  bccRecipients: string[]
): { isValid: boolean; to: string[]; cc: string[]; bcc: string[]; error?: string } => {
  const to = toRecipients.map(e => e.trim()).filter(e => e);
  const cc = ccRecipients.map(e => e.trim()).filter(e => e);
  const bcc = bccRecipients.map(e => e.trim()).filter(e => e);

  const allEmails = [...to, ...cc, ...bcc];
  for (const email of allEmails) {
    if (!isValidEmail(email)) {
      return { isValid: false, to: [], cc: [], bcc: [], error: `Invalid email format: ${email}` };
    }
  }

  const uniqueTo = Array.from(new Set(to.map(e => e.toLowerCase()))).map(e => to.find(orig => orig.toLowerCase() === e) || e);
  const uniqueCc = Array.from(new Set(cc.map(e => e.toLowerCase()))).map(e => cc.find(orig => orig.toLowerCase() === e) || e);
  const uniqueBcc = Array.from(new Set(bcc.map(e => e.toLowerCase()))).map(e => bcc.find(orig => orig.toLowerCase() === e) || e);

  const filteredCc = uniqueCc.filter(email => !uniqueTo.some(toEmail => toEmail.toLowerCase() === email.toLowerCase()));
  const filteredBcc = uniqueBcc.filter(
    email => !uniqueTo.some(toEmail => toEmail.toLowerCase() === email.toLowerCase()) && !filteredCc.some(ccEmail => ccEmail.toLowerCase() === email.toLowerCase())
  );

  if (uniqueTo.length === 0) {
    return { isValid: false, to: [], cc: [], bcc: [], error: 'At least one To recipient is required' };
  }

  return { isValid: true, to: uniqueTo, cc: filteredCc, bcc: filteredBcc };
};

const formatAustralianLocalPhone = (digits: string): string | null => {
  if (/^04\d{8}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (/^0[2378]\d{8}$/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  if (/^13\d{4}$/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }
  if (/^(1300|1800)\d{6}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return null;
};

const formatInternationalPhone = (digits: string): string => {
  const groups: string[] = [];
  let remaining = digits;
  while (remaining.length > 4) {
    groups.unshift(remaining.slice(-3));
    remaining = remaining.slice(0, -3);
  }
  groups.unshift(remaining);
  return `+${groups.join(' ')}`;
};

export const normalisePhoneNumber = (rawValue: any): NormalisedPhoneNumber => {
  const raw = String(rawValue ?? '').trim();
  if (!raw) {
    return { value: '', isValid: true };
  }
  if (/[eE][+-]?\d+/.test(raw) || /^\d+\.\d+$/.test(raw)) {
    return { value: raw, isValid: false, error: 'Phone number is stored in spreadsheet scientific or decimal format. Format the source phone column as Text and restore the complete digits before importing.' };
  }
  if (/[a-z]/i.test(raw)) {
    return { value: raw, isValid: false, error: 'Phone number contains letters. Enter the complete number using digits, spaces, brackets, hyphens, or a leading plus sign only.' };
  }

  let compact = raw.replace(/\u00a0/g, ' ').replace(/[\s().-]/g, '');
  if (compact.startsWith('0011')) {
    compact = `+${compact.slice(4)}`;
  } else if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`;
  }

  if (!/^\+?\d+$/.test(compact)) {
    return { value: raw, isValid: false, error: 'Phone number contains unsupported characters.' };
  }

  let digits = compact.replace(/\D/g, '');
  if (digits.startsWith('610') && digits.length === 12) {
    digits = `61${digits.slice(3)}`;
  }

  let localDigits = digits;
  if (digits.startsWith('61') && digits.length === 11) {
    localDigits = `0${digits.slice(2)}`;
  } else if (digits.length === 9 && /^[23478]/.test(digits)) {
    localDigits = `0${digits}`;
  }

  const australianPhone = formatAustralianLocalPhone(localDigits);
  if (australianPhone) {
    return { value: australianPhone, isValid: true };
  }

  if (/^[1-9]\d{7,14}$/.test(digits)) {
    return { value: formatInternationalPhone(digits), isValid: true };
  }

  return { value: raw, isValid: false, error: 'Phone number is not a recognised Australian number or a valid international number.' };
};

export const formatPhoneNumber = (value: any): string => {
  const result = normalisePhoneNumber(value);
  return result.isValid ? result.value : String(value ?? '').trim();
};

export const formatPhoneNumberForCsv = (value: any): string => formatPhoneNumber(value);

export const phoneDigitsForComparison = (value: any): string => {
  const result = normalisePhoneNumber(value);
  return (result.isValid ? result.value : String(value ?? '')).replace(/\D/g, '');
};